import {
  Text,
  Title,
  Card,
  Button,
  Group,
  ActionIcon,
  rem,
  useMantineColorScheme,
} from "@mantine/core"
import { match } from "ts-pattern"
import { UserPersonNameText } from "../../blocks/persons-and-users/User"
import { AbsentApprovalStatusIcon } from "./absent-approval-status-icon"
import { RenderApprovalResult } from "./-approval-cards/completed-approval-node"
import { ApproveOrRejectModal } from "./approve-modal"
import { DottedMenu } from "../../-components/dotted-menu"
import { useTranslations } from "../../utils/use-t"
import { useTranslation } from "react-i18next"
import { DefaultList } from "../../-components/default-list"
import { DefaultListItem } from "../../-components/default-list/list-item"
import { ApprovalStepDetailsModal } from "./approval-step-details-modal"
import { IconDotsVertical } from "@tabler/icons-react"
import { useBreakpoints } from "../../utils/use-breakpoints"
import {
  AbsenceRequestViewmodel,
  ApprovalAndWorkflowNode,
} from "../../@client/Theasoft_Absences"
import { AbsentApprovalNode } from "../../@client/Absents"

function Row({
  node,
  data,
  onReviewClicked,
  onDetailsClicked,
}: {
  node: ApprovalAndWorkflowNode
  data: AbsenceRequestViewmodel
  onReviewClicked: (item: AbsentApprovalNode) => void
  onDetailsClicked: (item: ApprovalAndWorkflowNode) => void
}) {
  const { xs } = useBreakpoints()
  const { colorScheme } = useMantineColorScheme()
  const { t } = useTranslation("app_server")
  const { approvalNode, workflowNode } = node
  const approvalDetails = match(approvalNode.details)
    .with({ Case: "Approval" }, (x) => x.Fields)
    .otherwise(() => null)
  if (approvalDetails === null) {
    return null
  }
  const isActive =
    node.workflowNode.status.Case === "Active" &&
    data.workflow.result.Case === "Pending"
  const icon = approvalDetails ? (
    <AbsentApprovalStatusIcon
      variant="transparent"
      isActive={isActive}
      status={approvalDetails.result}
    />
  ) : null
  const pendingApprovals = approvalDetails?.approvals
    .filter((v) => v.result.Case === "Pending")
    .map((approval, i) => (
      <>
        {i > 0 ? ", " : null}
        <UserPersonNameText id={approval.userId} />
      </>
    ))
  const completedApprovals = approvalDetails?.approvals
    .filter((v) => v.result.Case !== "Pending")
    .map((approval) => <RenderApprovalResult approval={approval} />)

  return (
    <DefaultListItem
      bg={
        isActive ? (colorScheme === "light" ? "blue.0" : "dark.8") : undefined
      }
    >
      {icon}
      <Group
        flex="1"
        style={{
          flexDirection: xs ? "column" : "row",
          alignItems: xs ? "start" : "center",
        }}
        gap={xs ? 2 : "xs"}
      >
        <Text fw={"bold"}>{workflowNode?.name}</Text>
        {completedApprovals.length === 0
          ? pendingApprovals
          : completedApprovals}
      </Group>
      <Group>
        <div>
          {data.permissions.canReview &&
          data.currentNode &&
          data.currentNode[0].nodeId === node.approvalNode.nodeId ? (
            <Button
              color="pink"
              onClick={() => {
                onReviewClicked(data.currentNode![0])
              }}
            >
              {t("ReviewNow")}
            </Button>
          ) : null}
        </div>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => onDetailsClicked(node)}
        >
          <IconDotsVertical style={{ width: rem(16), height: rem(16) }} />
        </ActionIcon>
      </Group>
    </DefaultListItem>
  )
}

export function ReviewersCard({
  data,
  onAddApprovalStepClicked,

  leaveRequestId,
}: {
  data: AbsenceRequestViewmodel
  onAddApprovalStepClicked?: () => void
  leaveRequestId: number
}) {
  const { absences } = useTranslations()
  const [reviewing, setReviewing] = React.useState<AbsentApprovalNode | null>(
    null
  )
  const [showingDetails, setShowDetails] =
    React.useState<ApprovalAndWorkflowNode | null>(null)

  return (
    <Card shadow="xs" withBorder>
      <Group justify="space-between" pr="xs">
        <Title order={4}>{absences.t("ApprovalSteps")}</Title>
        {data.permissions.canChangeApprovers ? (
          <DottedMenu
            items={[
              {
                children: absences.t("AddApprovalStep"),
                onClick: () => {
                  onAddApprovalStepClicked && onAddApprovalStepClicked()
                },
              },
            ]}
          />
        ) : null}
      </Group>
      <DefaultList
        selectable={false}
        mt="md"
        data={data.allNodes}
        divider
        renderItem={({ entity: v, index: i }) => (
          <Row
            key={i}
            node={v}
            data={data}
            onDetailsClicked={(item) => {
              setShowDetails(item)
            }}
            onReviewClicked={(item: AbsentApprovalNode) => setReviewing(item)}
          />
        )}
      />
      <ApprovalStepDetailsModal
        leaveRequestId={leaveRequestId}
        currentApprovalInfo={data}
        approvalNode={showingDetails}
        onApproveClicked={() => {
          setShowDetails(null)
        }}
        onClose={() => setShowDetails(null)}
      />

      <ApproveOrRejectModal
        leaveRequestId={leaveRequestId}
        approvalNode={reviewing}
        onApproveClicked={() => {
          setReviewing(null)
        }}
        onClose={() => setReviewing(null)}
      />
    </Card>
  )
}
